import { useForm, FormProvider, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, connect } from "react-redux";
import { useEffect, useState } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { ClaimDraftConfirmation, InProcessClaim } from "../../components/Modal";
import { PlanCardField } from "../../components/PlanCard";
import { createClaim, deleteClaimCreateNew } from "../../store/claim/actions";
import { PlanStatus, PlanTypes } from "../../types/plan";
import { navigateTo, classNames, formatCurrency } from "../../helper";
import RouteNames from "../../routes/type";
import { RootState } from "../../store/root-reducer";
import { BackButton, Button } from "../../components";
import { createAceClaim } from "../../store/ace-claim/actions";
import { claimStepsData } from "../CreateFppClaim/types";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import { ClaimScope } from "../../store/claim/types";
import { UserScope } from "../../store/user/types";
import { PiCheckBold } from "react-icons/pi";
import { WarrantyItem } from "../../types/warranty-item";
import Loader from "../../components/Loader";
import { searchPlans } from "../../store/user/actions";
import { AceClaimScope } from "../../store/ace-claim/types";

const DATE_FORMAT = "MMMM Do, YYYY";
const CONSUMER_PORTAL_ACE2_URL = process.env.REACT_APP_CONSUMER_PORTAL_ACE2_URL;

const savedSearchInfo = (() => {
  try {
    return JSON.parse(localStorage.getItem("searchInfo") || "{}");
  } catch (error) {
    return {};
  }
})();

function SelectPlan({ plans, loading }) {
  const methods = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const warranty_id = searchParams.get("warranty_id") || null;
  const claimId = searchParams.get("claimId") || null;
  const { t } = useTranslation();
  const selectedPlan = methods.watch("selectedPlan");
  const stepId: number = parseInt(searchParams.get("step") || "1");

  const [draftModal, setDraftModal] = useState({
    isShow: false,
    claimId: "",
  });

  const [openClaimInProcessModal, setOpenClaimInProcessModal] = useState({
    isShow: false,
    claimStatusCode: "",
  });

  const { control, setValue, clearErrors } = methods;

  useEffect(() => {
    const activePlans = plans.filter(
      (plan) => plan.planStatus === PlanStatus.ACTIVE
    );

    const selectedWarrantyId =
      activePlans.length === 1
        ? activePlans[0].id
        : warranty_id
          ? warranty_id
          : null;

    if (selectedWarrantyId) {
      setValue("selectedPlan", selectedWarrantyId, {
        shouldValidate: true,
      });
      clearErrors("selectedPlan");
    }
    if (savedSearchInfo?.search_type && plans.length <= 0) {
      dispatch(searchPlans(savedSearchInfo));
    } else if (plans.length <= 0) {
      navigateTo(RouteNames.NO_WARRANTY_FOUND);
    }
  }, [clearErrors, dispatch, plans, setValue, warranty_id]);

  const onSubmit = (values) => {
    const selectedPlanId = values.selectedPlan;
    console.log("selectedPlanId:", selectedPlanId);
    const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
    if (selectedPlan?.planType === PlanTypes.FPP) {
      if (claimId && +warranty_id === +selectedPlanId) {
        navigate(
          `/${RouteNames.CREATE_CLAIM}?step=2&claimId=${
            claimId
          }&warranty_id=${warranty_id}`
        );
      } else {
        dispatch(
          createClaim({
            warranty_id: selectedPlanId,
            showDraftModal: (claimId) =>
              setDraftModal({ isShow: true, claimId }),
            showClaimInProcessModal: (claimStatusCode) =>
              setOpenClaimInProcessModal({
                isShow: true,
                claimStatusCode,
              }),
          })
        );
      }
    } else if (selectedPlan?.planType === PlanTypes.ACE) {
      if (claimId && +warranty_id === +selectedPlanId) {
        navigate(
          `/${RouteNames.CREATE_ACE_CLAIM}?step=2&claim_id=${
            claimId
          }&warranty_id=${warranty_id}&cust_id=${selectedPlan.customerId}`
        );
      } else {
        dispatch(
          createAceClaim({
            warranty_id: selectedPlanId,
          })
        );
      }
    } else if (selectedPlan.planType === PlanTypes.LEGACY) {
      window.open(
        `${CONSUMER_PORTAL_ACE2_URL}/${+selectedPlanId}?fpp_id=true`,
        "_blank"
      );
    }
  };

  return (
    <div className="px-4 space-y-3">
      <div className="mt-4 flex justify-between items-center">
        <BackButton />
        <div className="flex flex-row gap-2">
          {claimStepsData.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA] "
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-8">
        <h3 className="text-2xl font-semibold">{t("Select your Plan")}</h3>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Loader isLoading={loading} />
          <Controller
            name="selectedPlan"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <RadioGroup value={value} onChange={onChange}>
                <div className="space-y-3 rounded-xl bg-white">
                  {plans.map((plan) => (
                    <Radio
                      key={plan.id}
                      value={plan.id}
                      disabled={plan.planStatus !== PlanStatus.ACTIVE}
                      className={({ checked }) =>
                        classNames(
                          checked
                            ? "bg-blue-light border-brand-color z-10"
                            : "border-border-gray",
                          "relative block border overflow-hidden cursor-pointer outline-none transition-all w-full select-none rounded-xl"
                        )
                      }
                    >
                      {({ checked, disabled }) => (
                        <>
                          <div
                            className={classNames(
                              "flex px-2 py-2 justify-start flex-row items-start gap-1 bg-[#2D6CCA] bg-opacity-10 w-full",
                              disabled && "opacity-65 cursor-not-allowed"
                            )}
                          >
                            <div
                              className={classNames(
                                checked
                                  ? "bg-brand-color border-transparent"
                                  : "bg-white border-gray-300",
                                "h-4 mt-2 w-4 shrink-0 cursor-pointer rounded-full border flex items-center justify-center transition-all"
                              )}
                              aria-hidden="true"
                            >
                              <span className="rounded-full bg-white w-1.5 h-1.5" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-[#2D6CCA]">
                                {plan.programName || plan.number}
                              </h3>
                              {plan.expirationDate && (
                                <p className="text-black text-sm font-normal">
                                  Active until{" "}
                                  {moment(plan.expirationDate).format(
                                    DATE_FORMAT
                                  )}
                                </p>
                              )}
                            </div>
                            <p
                              className={classNames(
                                "ml-auto text-white rounded-full px-2 text-sm bg-black",
                                plan.planStatus === PlanStatus.ACTIVE &&
                                  "bg-blue-500",
                                plan.planStatus === PlanStatus.CANCELLED &&
                                  "bg-red-400",
                                plan.planStatus === PlanStatus.Expired &&
                                  "bg-gray-400",
                                [
                                  PlanStatus.CancelCustomerReview,
                                  PlanStatus.CancelDealerReview,
                                ].includes(plan.planStatus) && "bg-yellow-400"
                              )}
                            >
                              {plan.planStatus}
                            </p>
                          </div>
                          <div
                            className={classNames(
                              "bg-white grid gap-1.5 divide-y-[1px] divide-[#F0F0F0] px-2 py-2",
                              disabled && "opacity-65 cursor-not-allowed"
                            )}
                          >
                            <PlanCardField
                              label={"Customer Name"}
                              value={plan.customerName}
                            />
                            <PlanCardField
                              label={"Purchase Date"}
                              value={plan.purchaseDate}
                              date
                            />
                            <PlanCardField
                              label={"Delivery Date"}
                              value={plan.deliveryDate}
                              date
                            />
                            <PlanCardField
                              label={"Total Purchase Price"}
                              value={plan.totalPrice}
                              price
                            />
                            <PlanCardField
                              label={"Store Code"}
                              value={plan.store}
                            />
                            <PlanCardField
                              label={"Invoice"}
                              value={plan.invoiceId}
                            />
                          </div>
                          <WarrantyItemsTable
                            items={plan.warrantyItems || []}
                          />
                        </>
                      )}
                    </Radio>
                  ))}
                </div>
              </RadioGroup>
            )}
          />
          <Button
            className="my-4"
            submit
            disabled={!selectedPlan}
            isLoading={loading}
          >
            Submit
          </Button>
        </form>
      </FormProvider>

      <ClaimDraftConfirmation
        isOpen={draftModal?.isShow}
        onClose={() => setDraftModal({ isShow: false, claimId: null })}
        onYesClick={() => {
          navigate(
            `/${RouteNames.CREATE_CLAIM}?step=2&claimId=${
              draftModal.claimId
            }&warranty_id=${methods.getValues("selectedPlan")}`
          );
          setDraftModal({ isShow: false, claimId: null });
        }}
        onNoClick={() => {
          dispatch(
            deleteClaimCreateNew({
              warranty_id: methods.getValues("selectedPlan"),
              claimId: draftModal.claimId,
            })
          );
          setDraftModal({ isShow: false, claimId: null });
        }}
      />

      <InProcessClaim
        isOpen={openClaimInProcessModal.isShow}
        claimStatusCode={openClaimInProcessModal.claimStatusCode}
        onClose={() =>
          setOpenClaimInProcessModal({
            isShow: false,
            claimStatusCode: null,
          })
        }
      />
    </div>
  );
}

function WarrantyItemsTable({ items }) {
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();
  const visibleItems = showAll ? items : items.slice(0, 3);

  return (
    <>
      <p className="text-sm text-blue-800 p-2">
        {t(
          "If you do not see your item listed below, you can still add the item, but we will require a copy of your invoice/sales receipt to proceed with your claim."
        )}
      </p>
      <div className="bg-white overflow-x-auto">
        <table className="min-w-full text-left text-sm border rounded-lg">
          <thead className="bg-gray-100 font-medium">
            <tr>
              <th className="p-2">{t("Item type")}</th>
              <th className="p-2">{t("Model")}</th>
              <th className="p-2">{t("SKU")}</th>
              <th className="p-2">{t("Vendor")}</th>
              <th className="p-2">{t("Material")}</th>
              <th className="p-2">{t("Price")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item: WarrantyItem, index) => (
              <tr key={index} className="border-t">
                <td className="p-2">{item.type.name || "N/A"}</td>
                <td className="p-2">{item.modelNumber || "N/A"}</td>
                <td className="p-2">{item.sku || "N/A"}</td>
                <td className="p-2">{item.vendor || "N/A"}</td>
                <td className="p-2">{item.material.name || "N/A"}</td>
                <td className="p-2">
                  {formatCurrency(item.itemPrice) || "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length > 3 && (
        <div className="px-2 py-1 text-right">
          <button
            onClick={() => setShowAll(!showAll)}
            type="button"
            className="text-blue-600 hover:underline text-sm"
          >
            {showAll ? t("Show Less") : t("Show More")}
          </button>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  plans: state.user.plans,
  loading:
    isScopeLoading(state.scopeLoading, ClaimScope) ||
    isScopeLoading(state.scopeLoading, UserScope) ||
    isScopeLoading(state.scopeLoading, AceClaimScope),
});

export default connect(mapStateToProps)(SelectPlan);
